import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { apiService } from "@/lib/api-service";
import type { Project } from "@/types/project.types";

interface ProjectDeleteDialogProps {
  project: Project;
}

export function ProjectDeleteDialog({ project }: ProjectDeleteDialogProps) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const deleteProjectMutation = useMutation({
    mutationFn: async () => {
      await apiService.delete(`/projects/${project.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setOpen(false);
      // Navigate back to projects list
      navigate({ to: "/projects" });
    },
    onError: (error: unknown) => {
      console.error("Failed to delete project:", error);
      // You could add toast notification here
    },
  });

  const handleDelete = () => {
    deleteProjectMutation.mutate();
  };

  const onOpenChange = (open: boolean) => {
    if (deleteProjectMutation.isPending) return;

    setOpen(open);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="destructive" size="lg" className="w-full">
          <Trash2 className="h-4 w-4" />
          Delete Project
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <span>Delete Project</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete the project{" "}
            <span className="font-semibold text-foreground">
              "{project.name}"
            </span>
            ? This action cannot be undone.
          </p>
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <p className="text-sm text-destructive">
              <strong>Warning:</strong> This will permanently delete the project
              and all associated data including overtime entries and member
              assignments.
            </p>
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={deleteProjectMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteProjectMutation.isPending}
            >
              {deleteProjectMutation.isPending
                ? "Deleting..."
                : "Delete Project"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
